// Updated App.js - Fixed navigation, warnings, and animations for all themes
import React, { useState, createContext, useContext, useEffect, useRef, useCallback } from 'react';
import './App.css';
import robethoImage from './robetho.jpg';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLinkedin, faWhatsapp, faGithub, faXTwitter, 
  faNodeJs, faReact, faDocker, faPython, faAws, faWordpress,
  faCss3, faJs, faPhp, faAngular, faFlutter, faGitAlt,
  faLinux, faJira
} from '@fortawesome/free-brands-svg-icons';
import { 
  faEnvelope, faPhone, faMapMarkerAlt, faHome, faUser, 
  faCode, faProjectDiagram, faGraduationCap, faAddressBook, 
  faSun, faMoon, faCrown, faStar, faCodeBranch, faEye,
  faBriefcase, faRocket, faLaptopCode, faGlobe,
  faDatabase, faShieldAlt, faUsers,
  faSearch, faBolt, faCalendarAlt,
  faShoppingCart, faCheckCircle, faServer,
  faFire, faPalette, faCogs,
  faMagic, faHeart, faMedal, 
  faTrophy, faBook, faMicrochip,
  faBrain, faClock, faArrowRight,
  faChevronRight, faChevronLeft, faTimes, faBars,
  faGem, faCircle, faSquare, faCube,
  faMobile, faCloud, faLock, faChartLine, faVideo, faDownload, faFilePdf, faFileAlt,
  faCar, faRobot, faCamera, 
  faFingerprint, faQrcode,
   faUserCheck, faCertificate, faSync
} from '@fortawesome/free-solid-svg-icons';

// Theme Context
const ThemeContext = createContext();

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('gold');
  
  const toggleTheme = () => {
    if (theme === 'gold') setTheme('light');
    else if (theme === 'light') setTheme('dark');
    else setTheme('gold');
  };

  const setSpecificTheme = (newTheme) => {
    setTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setSpecificTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Language Switcher Component
const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="language-selector" ref={dropdownRef}>
      <button className="language-button" onClick={() => setIsOpen(!isOpen)}>
        <span className="language-flag">{i18n.language === 'en' ? '🇬🇧' : '🇹🇿'}</span>
        <span className="language-text">{i18n.language === 'en' ? 'EN' : 'SW'}</span>
        <FontAwesomeIcon icon={isOpen ? faChevronRight : faChevronLeft} className="language-arrow" />
      </button>
      {isOpen && (
        <div className="language-dropdown">
          <button onClick={() => { i18n.changeLanguage('en'); setIsOpen(false); }} className="language-option">
            <span className="option-flag">🇬🇧</span>
            <span className="option-text">English</span>
          </button>
          <button onClick={() => { i18n.changeLanguage('sw'); setIsOpen(false); }} className="language-option">
            <span className="option-flag">🇹🇿</span>
            <span className="option-text">Kiswahili</span>
          </button>
        </div>
      )}
    </div>
  );
};

// Theme Switcher Component
const ThemeSwitcher = () => {
  const { theme, setSpecificTheme } = useContext(ThemeContext);

  return (
    <div className="theme-switcher">
      <button className={`theme-btn ${theme === 'light' ? 'active' : ''}`} onClick={() => setSpecificTheme('light')} title="Light Mode">
        <FontAwesomeIcon icon={faSun} className="theme-icon-sun" />
      </button>
      <button className={`theme-btn ${theme === 'dark' ? 'active' : ''}`} onClick={() => setSpecificTheme('dark')} title="Dark Mode">
        <FontAwesomeIcon icon={faMoon} className="theme-icon-moon" />
      </button>
      <button className={`theme-btn ${theme === 'gold' ? 'active' : ''}`} onClick={() => setSpecificTheme('gold')} title="Gold Mode">
        <FontAwesomeIcon icon={faCrown} className="theme-icon-crown" />
      </button>
    </div>
  );
};

// Project Modal Component
const ProjectModal = ({ project, onClose }) => {
  const { t } = useTranslation();
  const modalRef = useRef();
  const [isVisible, setIsVisible] = useState(false);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  useEffect(() => {
    setIsVisible(true);
    
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        handleClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClose]);

  if (!project) return null;

  return (
    <div className={`modal-overlay ${isVisible ? 'visible' : ''}`} onClick={handleClose}>
      <div className={`modal-content ${isVisible ? 'visible' : ''}`} ref={modalRef} onClick={e => e.stopPropagation()}>
        <button className="close-button" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        
        <div className="modal-header" style={{ background: `linear-gradient(135deg, ${project.color}20, ${project.color}40)` }}>
          <div className="modal-icon-wrapper" style={{ backgroundColor: project.color }}>
            <FontAwesomeIcon icon={project.icon} className="modal-icon" />
          </div>
          <h3>{t(project.title)}</h3>
        </div>
        
        <div className="modal-body">
          <p className="modal-description">{t(project.fullDetails)}</p>
          
          {project.technologies && (
            <div className="modal-section">
              <h4><FontAwesomeIcon icon={faMicrochip} className="section-icon" /> {t('technologies_used')}</h4>
              <div className="tech-tags">
                {project.technologies.map((tech, index) => (
                  <span key={index} className="tech-tag" style={{ backgroundColor: project.color + '15', color: project.color, borderColor: project.color + '30' }}>
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}

          {project.challenges && (
            <div className="modal-section">
              <h4><FontAwesomeIcon icon={faBrain} className="section-icon" /> {t('challenges_and_solutions')}</h4>
              <p className="challenges-text">{t(project.challenges)}</p>
            </div>
          )}

          <div className="modal-section">
            <h4><FontAwesomeIcon icon={faStar} className="section-icon" /> {t('key_features')}</h4>
            <ul className="feature-list">
              {project.features.map((feature, index) => (
                <li key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <FontAwesomeIcon icon={faCheckCircle} className="feature-check" style={{ color: project.color }} />
                  <span>{t(feature)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="modal-footer">
          {project.demoLink && (
            <a href={project.demoLink} target="_blank" rel="noopener noreferrer" className="modal-link demo-link" style={{ backgroundColor: project.color }}>
              <FontAwesomeIcon icon={faGlobe} /> <span>{t('view_demo')}</span>
            </a>
          )}
          {project.githubLink && (
            <a href={project.githubLink} target="_blank" rel="noopener noreferrer" className="modal-link github-link" style={{ borderColor: project.color, color: project.color }}>
              <FontAwesomeIcon icon={faGithub} /> <span>{t('view_on_github')}</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// Appointment Scheduler Component
const AppointmentScheduler = () => {
  const { t } = useTranslation();
  
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    return () => { 
      if (document.body.contains(script)) {
        document.body.removeChild(script); 
      }
    };
  }, []);

  return (
    <div className="appointment-scheduler">
      <h3 className="section-subtitle">
        <FontAwesomeIcon icon={faClock} className="subtitle-icon" />
        {t('book_session')}
      </h3>
      <div 
        className="calendly-inline-widget" 
        data-url="https://calendly.com/lyandorobetho/30min" 
        style={{ minWidth: '320px', height: '700px' }}
      ></div>
    </div>
  );
};

// Contact Form Component
const ContactForm = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(""); 
    setIsSuccess(false); 

    if (!validateEmail(formData.email)) {
      setStatus("Tafadhali weka barua pepe sahihi.");
      setIsSuccess(false);
      return;
    }

    setIsLoading(true); 
    
    const formspreeUrl = "https://formspree.io/f/mzzjpbll"; 
    
    try {
      const response = await fetch(formspreeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus("Asante! Ujumbe wako umefika.");
        setIsSuccess(true);
        setFormData({ name: '', email: '', subject: '', message: '' }); 
      } else {
        const data = await response.json();
        if (data.errors) {
          setStatus(data.errors.map(err => err.message).join(", "));
        } else {
          setStatus("Samahani, kulikuwa na tatizo. Tafadhali jaribu tena.");
        }
        setIsSuccess(false);
      }
    } catch (error) {
      setStatus("Samahani, kulikuwa na tatizo. Tafadhali jaribu tena.");
      setIsSuccess(false);
    } finally {
      setIsLoading(false); 
    }
  };

  return (
    <div className="contact-form-container">
      <h3 className="sub-title">
        <FontAwesomeIcon icon={faEnvelope} className="subtitle-icon" />
        {t('send_message')}
      </h3>
      <p className="sub-description">{t('send_message_desc')}</p>
      <form onSubmit={handleSubmit} className="contact-form">
        <div className="form-group">
          <input 
            type="text" 
            name="name"
            placeholder={t('your_name')} 
            value={formData.name}
            onChange={handleChange}
            required
          />
          <span className="focus-border"></span>
        </div>
        <div className="form-group">
          <input 
            type="email" 
            name="email"
            placeholder={t('your_email')} 
            value={formData.email}
            onChange={handleChange}
            required
          />
          <span className="focus-border"></span>
        </div>
        <div className="form-group">
          <input 
            type="text" 
            name="subject"
            placeholder={t('subject')}
            value={formData.subject}
            onChange={handleChange}
          />
          <span className="focus-border"></span>
        </div>
        <div className="form-group">
          <textarea 
            name="message"
            placeholder={t('your_message')}
            value={formData.message}
            onChange={handleChange}
            required
          ></textarea>
          <span className="focus-border"></span>
        </div>
        <button type="submit" disabled={isLoading} className="submit-btn">
          {isLoading ? (
            <div className="spinner"></div> 
          ) : (
            <>
              <span>{t('submit')}</span>
              <FontAwesomeIcon icon={faArrowRight} className="btn-icon" />
            </>
          )}
        </button>
      </form>
      {status && (
        <div className={`form-status ${isSuccess ? 'success' : 'error'}`}>
          <FontAwesomeIcon icon={isSuccess ? faCheckCircle : faTimes} />
          <span>{status}</span>
        </div>
      )}
    </div>
  );
};

// Experience Tree Item Component
const ExperienceTreeItem = ({ title, subtitle, description, date, type, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const itemRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );

    const currentRef = itemRef.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div 
      ref={itemRef}
      className={`tree-item ${type} ${isVisible ? 'visible' : ''}`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <div className="tree-item-content">
        <h4>{title}</h4>
        <h5>{subtitle}</h5>
        <p>{description}</p>
        {date && (
          <div className="item-date">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <small>{date}</small>
          </div>
        )}
      </div>
    </div>
  );
};

// Floating Particles Component - Now works for all themes
const FloatingParticles = () => {
  const { theme } = useContext(ThemeContext);
  
  const getIconColor = () => {
    if (theme === 'light') return 'rgba(46, 204, 113, 0.3)';
    if (theme === 'dark') return 'rgba(46, 204, 113, 0.3)';
    return 'rgba(255, 215, 0, 0.4)';
  };

  const icons = [faStar, faGem, faCircle, faSquare, faCube, faCrown];

  return (
    <div className="floating-particles">
      {[...Array(20)].map((_, i) => {
        const icon = icons[Math.floor(Math.random() * icons.length)];
        return (
          <div
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${15 + Math.random() * 10}s`,
              fontSize: `${0.5 + Math.random() * 1.5}rem`,
              opacity: 0.1 + Math.random() * 0.2,
              color: getIconColor()
            }}
          >
            <FontAwesomeIcon icon={icon} />
          </div>
        );
      })}
    </div>
  );
};

const AppContent = () => {
  const { t } = useTranslation();
  const { theme } = useContext(ThemeContext);
  const [activeTab, setActiveTab] = useState('vision');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeNav, setActiveNav] = useState('hero');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const projectsPerPage = 6;

  // Create refs for each section
  const sectionRefs = {
    hero: useRef(null),
    about: useRef(null),
    skills: useRef(null),
    experience: useRef(null),
    projects: useRef(null),
    contact: useRef(null)
  };

  useEffect(() => {
    document.body.className = `${theme}-mode-body`;
  }, [theme]);

  // Update active nav based on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'about', 'skills', 'experience', 'projects', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop - 100 && 
              scrollPosition < offsetTop + offsetHeight - 100) {
            if (activeNav !== section) {
              setActiveNav(section);
            }
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeNav]);

  // On component mount, check URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const sectionId = hash.replace('#', '');
      const element = document.getElementById(sectionId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setActiveNav(sectionId);
        }, 100);
      }
    }
  }, []);

  // Scroll to section function
  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    
    const targetElement = document.getElementById(sectionId);
    if (targetElement) {
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 80;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      setActiveNav(sectionId);
      setIsMobileMenuOpen(false);
      
      window.history.pushState(null, null, `#${sectionId}`);
    }
  };

  const frontendSkills = [
    { name: 'HTML5', icon: faCode, color: '#E44D26' },
    { name: 'CSS3', icon: faCss3, color: '#264DE4' },
    { name: 'JavaScript', icon: faJs, color: '#F7DF1E' },
    { name: 'React.js', icon: faReact, color: '#61DAFB' },
    { name: 'Angular', icon: faAngular, color: '#DD0031' },
    { name: 'Flutter', icon: faFlutter, color: '#02569B' },
    { name: 'Bootstrap', icon: faCode, color: '#7952B3' },
  ];

  const backendSkills = [
    { name: 'Node.js', icon: faNodeJs, color: '#339933' },
    { name: 'Python', icon: faPython, color: '#3776AB' },
    { name: 'PHP', icon: faPhp, color: '#777BB4' },
    { name: 'Laravel', icon: faPhp, color: '#FF2D20' },
    { name: 'CodeIgniter', icon: faPhp, color: '#DD4814' },
  ];

  const databaseSkills = [
    { name: 'Firebase', icon: faFire, color: '#FFCA28' },
    { name: 'PostgreSQL', icon: faDatabase, color: '#336791' },
    { name: 'MySQL', icon: faDatabase, color: '#4479A1' },
    { name: 'MongoDB', icon: faDatabase, color: '#47A248' },
  ];

  const devopsSkills = [
    { name: 'Git', icon: faGitAlt, color: '#F05032' },
    { name: 'GitHub', icon: faGithub, color: '#181717' },
    { name: 'AWS', icon: faAws, color: '#FF9900' },
    { name: 'Docker', icon: faDocker, color: '#2496ED' },
    { name: 'Linux', icon: faLinux, color: '#FCC624' },
    { name: 'Jira', icon: faJira, color: '#0052CC' },
  ];

  const experienceData = {
    education: [
      {
        title: t('bachelor_title'),
        subtitle: t('bachelor_institution'),
        description: t('bachelor_desc'),
        date: '2023 - Present',
        type: 'education'
      },
      {
        title: t('diploma_title'),
        subtitle: t('diploma_institution'),
        description: t('diploma_desc'),
        date: '2020 - 2023',
        type: 'education'
      }
    ],
    work: [
      {
        title: t('backend_experience_title'),
        subtitle: t('backend_experience_type'),
        description: t('backend_experience_desc'),
        date: '2022 - Present',
        type: 'work'
      },
      {
        title: t('freelance_title'),
        subtitle: t('freelance_type'),
        description: t('freelance_desc'),
        date: '2022 - Present',
        type: 'work'
      },
      {
        title: t('telecom_experience_title'),
        subtitle: t('telecom_experience_type'),
        description: t('telecom_experience_desc'),
        date: '2025 - present',
        type: 'work'
      }
    ]
  };

const projectList = [
    { 
      id: 1, 
      title: 'lost_finder_app_title', 
      description: 'lost_finder_app_desc', 
      fullDetails: 'lost_finder_app_full',
      technologies: ['Flutter', 'Firebase', 'Dart', 'Cloud Messaging', 'AI/ML'],
      challenges: 'lost_finder_app_challenges',
      features: [
        'Vitu Vinavyotafutika', 
        'Ulinganifu Otomatiki', 
        'Arifa za Kusukuma', 
        'Ripoti Kitu Kilichopatikana', 
        'Ufuatiliaji wa Eneo'
      ],
      githubLink: 'https://github.com/Robetho/lost_finder',
      demoLink: '#',
      icon: faSearch,
      color: '#FF6B6B'
    },
    { 
      id: 2, 
      title: 'online_application_system_title', 
      description: 'online_application_system_desc', 
      fullDetails: 'online_application_system_full',
      technologies: ['Laravel', 'PHP', 'MySQL', 'Bootstrap', 'Redis'],
      challenges: 'online_application_system_challenges',
      features: [
        'Usajili Mtandaoni', 
        'Upakiaji wa Hati', 
        'Ufuatiliaji Otomatiki', 
        'Mfumo wa Arifa', 
        'Hifadhidata Salama', 
        'Ujumuishaji wa Malipo'
      ],
      githubLink: 'https://github.com/Robetho/oas',
      demoLink: '#',
      icon: faGlobe,
      color: '#4ECDC4'
    },
    { 
      id: 3, 
      title: 'sims_title', 
      description: 'sims_desc', 
      fullDetails: 'sims_full',
      technologies: ['CodeIgniter', 'PHP', 'MySQL', 'Bootstrap', 'jQuery'],
      challenges: 'sims_challenges',
      features: [
        'Usajili wa Wanafunzi', 
        'Usimamizi wa Madarasa na Masomo', 
        'Matokeo ya Mitihani', 
        'Usimamizi wa Ada', 
        'Majukumu ya Watumiaji', 
        'Ripoti na Uchambuzi'
      ],
      githubLink: 'https://github.com/Robetho/Buhalahala-sims',
      demoLink: '#',
      icon: faLaptopCode,
      color: '#FFE66D'
    },
    { 
      id: 4, 
      title: 'academic_platform_title', 
      description: 'academic_platform_desc', 
      fullDetails: 'academic_platform_full',
      technologies: ['PHP', 'MySQL', 'Bootstrap', 'JavaScript', 'AJAX'],
      challenges: 'academic_platform_challenges',
      features: [
        'Mawasiliano ya Muda Halisi', 
        'Ugavi wa Rasilimali', 
        'Zana za Usimamizi wa Miradi', 
        'Usimamizi wa Kazi', 
        'Ushirikiano wa Hati'
      ],
      githubLink: 'https://github.com/Robetho/academic-collaborative-platform',
      demoLink: '#',
      icon: faUsers,
      color: '#A37BBA'
    },
    { 
      id: 5, 
      title: 'event_schedule_title', 
      description: 'event_schedule_desc', 
      fullDetails: 'event_schedule_full',
      technologies: ['PHP', 'MySQL', 'Bootstrap', 'jQuery', 'AJAX'],
      challenges: 'event_schedule_challenges',
      features: [
        'Upangaji wa Matukio', 
        'Usimamizi wa Kumbi', 
        'Ufuatiliaji wa Washiriki', 
        'Mfumo wa Arifa', 
        'Ujumuishaji wa Kalenda'
      ],
      githubLink: 'https://github.com/Robetho/campus-event-schedule',
      demoLink: '#',
      icon: faCalendarAlt,
      color: '#F9A26C'
    },
    { 
      id: 6, 
      title: 'hall_event_title', 
      description: 'hall_event_desc', 
      fullDetails: 'hall_event_full',
      technologies: ['PHP', 'MySQL', 'Bootstrap', 'JavaScript', 'Stripe API'],
      challenges: 'hall_event_challenges',
      features: [
        'Usimamizi wa Uhifadhi', 
        'Uratibu wa Vifaa', 
        'Mfumo wa Tiketi', 
        'Paneli ya Mtumiaji na Msimamizi', 
        'Usindikaji wa Malipo'
      ],
      githubLink: 'https://github.com/Robetho/event-management-platform',
      demoLink: '#',
      icon: faRocket,
      color: '#6C5B7B'
    },
    { 
      id: 7, 
      title: 'furniture_ecommerce_title', 
      description: 'furniture_ecommerce_desc', 
      fullDetails: 'furniture_ecommerce_full',
      technologies: ['Angular', 'TypeScript', 'CSS3', 'HTML5', 'RxJS', 'REST API'],
      challenges: 'furniture_ecommerce_challenges',
      features: [
        'Mkusanyiko wa Bidhaa wa Kifahari', 
        'Kikapu cha Ununuzi', 
        'Malipo Salama', 
        'Usimamizi wa Maagizo', 
        'Mapitio ya Wateja', 
        'Paneli ya Msimamizi'
      ],
      githubLink: 'https://github.com/Robetho/mkarimugeneralsupply',
      demoLink: '#',
      icon: faShoppingCart,
      color: '#8B4513'
    },
    { 
      id: 8, 
      title: 'voting_system_title', 
      description: 'voting_system_desc', 
      fullDetails: 'voting_system_full',
      technologies: ['PHP', 'MySQL', 'Bootstrap', 'JavaScript', 'Chart.js'],
      challenges: 'voting_system_challenges',
      features: [
        'Uthibitishaji Salama', 
        'Matokeo ya Muda Halisi', 
        'Usimbaji wa Data', 
        'Dashibodi ya Msimamizi', 
        'Uthibitishaji wa Blockchain'
      ],
      githubLink: 'https://github.com/Robetho/SfuscoVotingSystem',
      demoLink: '#',
      icon: faShieldAlt,
      color: '#F67280'
    },
    { 
      id: 9, 
      title: 'clearance_system_title', 
      description: 'clearance_system_desc', 
      fullDetails: 'clearance_system_full',
      technologies: ['Laravel', 'PHP', 'MySQL', 'Bootstrap', 'PDF Generator', 'TCPDF'],
      challenges: 'clearance_system_challenges',
      features: [
        'Uthibitishaji wa Wanafunzi', 
        'Kufutwa kwa Idara', 
        'Kufutwa kwa Maktaba', 
        'Kufutwa kwa Fedha', 
        'Uzalishaji wa PDF', 
        'Ufuatiliaji wa Wahitimu'
      ],
      githubLink: 'https://github.com/Robetho/sfuchas-student-clearance-system',
      demoLink: '#',
      icon: faGraduationCap,
      color: '#27ae60'
    },
    { 
      id: 10, 
      title: 'streaming_web_api_title', 
      description: 'streaming_web_api_desc', 
      fullDetails: 'streaming_web_api_full',
      technologies: ['Flutter', 'Dart', 'PHP', 'MySQL', 'JavaScript', 'CSS3', 'REST API'],
      challenges: 'streaming_web_api_challenges',
      features: [
        'Utiririshaji wa Video', 
        'Ufikiaji wa Majukwaa Mbalimbali', 
        'Ujumuishaji wa API', 
        'Ulandanisho wa Simu na Wavuti', 
        'Usimamizi wa Maudhui', 
        'Uthibitishaji wa Mtumiaji'
      ],
      githubLink: 'https://github.com/Robetho/streaming_web_api',
      demoLink: '#',
      icon: faVideo,
      color: '#3498db'
    },
    { 
      id: 11, 
      title: 'e_learning_platform_title',  // ← key kutoka translation
      description: 'e_learning_platform_desc',  // ← key kutoka translation
      fullDetails: 'e_learning_platform_full',  // ← key kutoka translation
      technologies: ['PHP', 'MySQL', 'JavaScript', 'Bootstrap', 'AJAX', 'Chart.js', 'PDF Generator'],
      challenges: 'e_learning_platform_challenges',  // ← key kutoka translation
      features: [
        'assignment_management',  // ← key kutoka translation
        'peer_review_system',
        'plagiarism_detection',
        'rubric_based_grading',
        'multi_user_roles',
        'real_time_notifications',
        'submission_tracking',
        'academic_calendar_integration'
      ],
      githubLink: 'https://github.com/Robetho/E-learning-platform',
      demoLink: '#',
      icon: faLaptopCode,
      color: '#0099cc'
    },
    {
      id: 12,
      title: 'ai_driving_evaluation_title',
      description: 'ai_driving_evaluation_desc',
      fullDetails: 'ai_driving_evaluation_full',
      technologies: ['Laravel', 'Flutter', 'MySQL', 'OpenCV', 'FaceNet', 'Blockchain', 'WebRTC', 'REST API'],
      challenges: 'ai_driving_evaluation_challenges',
      features: [
        'granular_assessment_feature',
        'facial_recognition_attendance',
        'blockchain_security',
        'geofencing_verification',
        'tra_api_integration',
        'real_time_feedback',
        'role_based_access',
        'automated_certification'
      ],
      githubLink: '#',  // ← Badilisha kuwa # kwa sababu repo ni private
      demoLink: '#',
      icon: faCar,
      color: '#ff6600'
    },
    { 
      id: 13, 
      title: 'student_industrial_training_title', 
      description: 'student_industrial_training_desc', 
      fullDetails: 'student_industrial_training_full',
      technologies: ['PHP', 'MySQL', 'HTML5', 'CSS3', 'JavaScript', 'Bootstrap', 'AJAX', 'REST API'],
      challenges: 'student_industrial_training_challenges',
      features: [
        'student_registration_feature',
        'training_opportunities_feature',
        'online_application_feature',
        'weekly_logbook_submission',
        'real_time_monitoring',
        'role_based_access',
        'supervisor_feedback_system',
        'performance_evaluation'
      ],
      githubLink: 'https://github.com/Robetho/student-industrial-training',
      demoLink: '#',
      icon: faLaptopCode,
      color: '#2ecc71'
    },
  ];

  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;
  const currentProjects = projectList.slice(indexOfFirstProject, indexOfLastProject);
  const totalPages = Math.ceil(projectList.length / projectsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const openModal = (project) => setSelectedProject(project);
  const closeModal = () => setSelectedProject(null);

  // Helper function to get technology icon
  const getTechIcon = (tech) => {
    const techLower = tech.toLowerCase();
    if (techLower.includes('flutter')) return faFlutter;
    if (techLower.includes('firebase')) return faFire;
    if (techLower.includes('laravel') || techLower.includes('php')) return faPhp;
    if (techLower.includes('codeigniter')) return faPhp;
    if (techLower.includes('mysql')) return faDatabase;
    if (techLower.includes('bootstrap')) return faCode;
    if (techLower.includes('jquery')) return faJs;
    return faCode;
  };

  return (
    <div className={`app-container ${theme}-mode`}>
      <FloatingParticles />
      
      {/* Mobile Menu Button */}
      <button className="mobile-menu-btn" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
        <FontAwesomeIcon icon={faBars} />
      </button>

      {/* Left Sidebar */}
      <aside className={`left-sidebar ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-content">
          <div className="sidebar-profile">
            <div className="profile-image-wrapper">
              <img src={robethoImage} alt="Robetho Lyando" className="sidebar-profile-image" />
              <div className="profile-image-glow"></div>
            </div>
            <h2 className="sidebar-name">ROBETHO LYANDO</h2>
            <p className="sidebar-title">
              <FontAwesomeIcon icon={faCode} className="title-icon" />
              {t('title')}
            </p>
          </div>

          {/* Navigation Menu */}
          <nav className="sidebar-nav">
            <ul className="sidebar-nav-list">
              <li className="sidebar-nav-item">
                <a href="#hero" className={`sidebar-nav-link ${activeNav === 'hero' ? 'active' : ''}`} onClick={(e) => scrollToSection(e, 'hero')}>
                  <FontAwesomeIcon icon={faHome} className="nav-icon" />
                  <span>{t('home')}</span>
                </a>
              </li>
              <li className="sidebar-nav-item">
                <a href="#about" className={`sidebar-nav-link ${activeNav === 'about' ? 'active' : ''}`} onClick={(e) => scrollToSection(e, 'about')}>
                  <FontAwesomeIcon icon={faUser} className="nav-icon" />
                  <span>{t('about')}</span>
                </a>
              </li>
              <li className="sidebar-nav-item">
                <a href="#skills" className={`sidebar-nav-link ${activeNav === 'skills' ? 'active' : ''}`} onClick={(e) => scrollToSection(e, 'skills')}>
                  <FontAwesomeIcon icon={faCode} className="nav-icon" />
                  <span>{t('skills')}</span>
                </a>
              </li>
              <li className="sidebar-nav-item">
                <a href="#experience" className={`sidebar-nav-link ${activeNav === 'experience' ? 'active' : ''}`} onClick={(e) => scrollToSection(e, 'experience')}>
                  <FontAwesomeIcon icon={faGraduationCap} className="nav-icon" />
                  <span>{t('experience')}</span>
                </a>
              </li>
              <li className="sidebar-nav-item">
                <a href="#projects" className={`sidebar-nav-link ${activeNav === 'projects' ? 'active' : ''}`} onClick={(e) => scrollToSection(e, 'projects')}>
                  <FontAwesomeIcon icon={faProjectDiagram} className="nav-icon" />
                  <span>{t('projects')}</span>
                </a>
              </li>
              <li className="sidebar-nav-item">
                <a href="#contact" className={`sidebar-nav-link ${activeNav === 'contact' ? 'active' : ''}`} onClick={(e) => scrollToSection(e, 'contact')}>
                  <FontAwesomeIcon icon={faAddressBook} className="nav-icon" />
                  <span>{t('contact')}</span>
                </a>
              </li>
            </ul>
          </nav>


          {/* Sidebar Footer */}
          <div className="sidebar-footer">
            <div className="sidebar-social-links">
              <a href="https://www.linkedin.com/in/robetho-lyando-07282636a/" target="_blank" rel="noopener noreferrer" className="sidebar-social-link" title="LinkedIn">
                <FontAwesomeIcon icon={faLinkedin} />
              </a>
              <a href="https://web.whatsapp.com/send?phone=+255689052127" target="_blank" rel="noopener noreferrer" className="sidebar-social-link" title="WhatsApp">
                <FontAwesomeIcon icon={faWhatsapp} />
              </a>
              <a href="mailto:robetholyando@gmail.com" target="_blank" rel="noopener noreferrer" className="sidebar-social-link" title="Email">
                <FontAwesomeIcon icon={faEnvelope} />
              </a>
              <a href="https://x.com/RLyando2119" target="_blank" rel="noopener noreferrer" className="sidebar-social-link" title="X (Twitter)">
                <FontAwesomeIcon icon={faXTwitter} />
              </a>
              <a href="http://github.com/Robetho/" target="_blank" rel="noopener noreferrer" className="sidebar-social-link" title="GitHub">
                <FontAwesomeIcon icon={faGithub} />
              </a>
            </div>

            <div className="cv-download-container">
              <a 
                href="/Robetho-Lyando.pdf" 
                download="Robetho_Lyando_CV.pdf"
                className="cv-download-btn"
                title="Download CV"
              >
                <FontAwesomeIcon icon={faFilePdf} />
                <span>Download CV</span>
              </a>
            </div>
            
            <div className="sidebar-controls">
              <ThemeSwitcher />
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </aside>

      {/* Right Main Content */}
      <main className="right-main">
        {/* HERO SECTION */}
        <section id="hero" className="section" ref={sectionRefs.hero}>
          <div className="container">
            <div className="welcome-card">
              <div className="welcome-header">
                <p className="greeting-text">
                  <FontAwesomeIcon icon={faHeart} className="greeting-icon" />
                  {t('welcome_to_portfolio')}
                </p>
                <p className="slogan-text">
                  <FontAwesomeIcon icon={faRocket} className="slogan-icon" />
                  {t('slogan_one')}
                </p>
              </div>
              <div className="welcome-message-container">
                <h2 className="welcome-message-title">
                  <FontAwesomeIcon icon={faStar} className="title-star" />
                  {t('welcome_message_title')}
                </h2>
                <p className="welcome-text">{t('welcome_message_text')}</p>
                <ul className="skills-list">
                  <li><span className="bullet">✨</span> {t('skill_one')}</li>
                  <li><span className="bullet">🚀</span> {t('skill_two')}</li>
                  <li><span className="bullet">💡</span> {t('skill_three')}</li>
                  <li><span className="bullet">⚡</span> {t('skill_four')}</li>
                </ul>
                <p className="contact-link">
                  <FontAwesomeIcon icon={faBriefcase} className="freelance-icon" />
                  {t('open_for_freelance')} - 
                  <a href="#contact" onClick={(e) => scrollToSection(e, 'contact')}>
                    {t('contact_me')}
                    <FontAwesomeIcon icon={faArrowRight} className="link-arrow" />
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ABOUT SECTION */}
        <section id="about" className="section" ref={sectionRefs.about}>
          <div className="container">
            <h2 className="section-title">
              <FontAwesomeIcon icon={faUser} className="title-icon" />
              {t('about_vision')}
            </h2>
            <div className="about-card">
              <div className="about-header">
                <div className="tab-buttons">
                  <button className={activeTab === 'vision' ? 'active' : ''} onClick={() => setActiveTab('vision')}>
                    <FontAwesomeIcon icon={faEye} /> {t('vision_mission_tab')}
                  </button>
                  <button className={activeTab === 'why' ? 'active' : ''} onClick={() => setActiveTab('why')}>
                    <FontAwesomeIcon icon={faMedal} /> {t('why_choose_me_tab')}
                  </button>
                </div>
              </div>
              <div className="about-content">
                {activeTab === 'vision' && (
                  <div className="tab-pane">
                    <h3><FontAwesomeIcon icon={faEye} className="pane-icon" /> {t('my_vision')}</h3>
                    <p>{t('vision_text')}</p>
                    <div className="mission-section">
                      <h3><FontAwesomeIcon icon={faRocket} className="pane-icon" /> {t('my_mission')}</h3>
                      <ul>
                        <li><FontAwesomeIcon icon={faCheckCircle} /> {t('mission_one')}</li>
                        <li><FontAwesomeIcon icon={faCheckCircle} /> {t('mission_two')}</li>
                        <li><FontAwesomeIcon icon={faCheckCircle} /> {t('mission_three')}</li>
                        <li><FontAwesomeIcon icon={faCheckCircle} /> {t('mission_four')}</li>
                      </ul>
                    </div>
                  </div>
                )}
                {activeTab === 'why' && (
                  <div className="tab-pane">
                    <h3><FontAwesomeIcon icon={faTrophy} className="pane-icon" /> {t('why_choose_me')}</h3>
                    <p>{t('why_choose_me_text_one')}</p>
                    <p>{t('why_choose_me_text_two')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* SKILLS SECTION */}
        <section id="skills" className="section" ref={sectionRefs.skills}>
          <div className="container">
            <h2 className="section-title">
              <FontAwesomeIcon icon={faCode} className="title-icon" />
              {t('technical_skills')}
            </h2>
            <div className="skills-content-container">
              <div className="skills-column">
                <h3><FontAwesomeIcon icon={faPalette} /> Frontend</h3>
                <div className="skills-tree">
                  {frontendSkills.map((skill, index) => (
                    <div className="skill-item" key={index}>
                      <FontAwesomeIcon icon={skill.icon} style={{ color: skill.color }} />
                      <span>{skill.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="skills-column">
                <h3><FontAwesomeIcon icon={faServer} /> Backend</h3>
                <div className="skills-tree">
                  {backendSkills.map((skill, index) => (
                    <div className="skill-item" key={index}>
                      <FontAwesomeIcon icon={skill.icon} style={{ color: skill.color }} />
                      <span>{skill.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="skills-column">
                <h3><FontAwesomeIcon icon={faDatabase} /> Databases</h3>
                <div className="skills-tree">
                  {databaseSkills.map((skill, index) => (
                    <div className="skill-item" key={index}>
                      <FontAwesomeIcon icon={skill.icon} style={{ color: skill.color }} />
                      <span>{skill.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="skills-column">
                <h3><FontAwesomeIcon icon={faCogs} /> DevOps & Tools</h3>
                <div className="skills-tree">
                  {devopsSkills.map((skill, index) => (
                    <div className="skill-item" key={index}>
                      <FontAwesomeIcon icon={skill.icon} style={{ color: skill.color }} />
                      <span>{skill.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* EXPERIENCE SECTION */}
        <section id="experience" className="section experience-tree-section" ref={sectionRefs.experience}>
          <div className="container">
            <h2 className="section-title">
              <FontAwesomeIcon icon={faGraduationCap} className="title-icon" />
              {t('experience_education')}
            </h2>
            <div className="experience-tree-container">
              <div className="tree-column">
                <h3><FontAwesomeIcon icon={faBook} /> {t('education')}</h3>
                <div className="tree-items">
                  {experienceData.education.map((item, index) => (
                    <ExperienceTreeItem key={index} index={index} {...item} />
                  ))}
                </div>
              </div>

              <div className="tree-column">
                <h3><FontAwesomeIcon icon={faBriefcase} /> {t('work_experience')}</h3>
                <div className="tree-items">
                  {experienceData.work.map((item, index) => (
                    <ExperienceTreeItem key={index} index={index} {...item} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PROJECTS SECTION */}
        <section id="projects" className="section" ref={sectionRefs.projects}>
          <div className="container">
            <h2 className="section-title">
              <FontAwesomeIcon icon={faProjectDiagram} className="title-icon" />
              {t('my_projects')}
            </h2>
            <div className="projects-grid">
              {currentProjects.map((project, index) => (
                <div className="project-card" key={index} style={{ '--project-color': project.color }}>
                  <div className="project-card-inner">
                    <div className="project-card-front">
                      <div className="project-icon-wrapper">
                        <FontAwesomeIcon icon={project.icon} className="project-icon" style={{ color: project.color }} />
                        <div className="project-icon-glow" style={{ backgroundColor: project.color }}></div>
                      </div>
                      <h3 style={{ color: project.color }}>{t(project.title)}</h3>
                      <div className="project-tech-tags">
                        {project.technologies.slice(0, 3).map((tech, i) => (
                          <span key={i} className="project-tech-tag" style={{ backgroundColor: project.color + '20' }}>
                            <FontAwesomeIcon icon={getTechIcon(tech)} className="tech-tag-icon" />
                            {tech}
                          </span>
                        ))}
                        {project.technologies.length > 3 && (
                          <span className="project-tech-tag">+{project.technologies.length - 3}</span>
                        )}
                      </div>
                      <p className="project-description">{t(project.description)}</p>
                      <div className="project-hover-indicator">
                        <FontAwesomeIcon icon={faMagic} />
                        <span>Hover to explore</span>
                      </div>
                    </div>
                    <div className="project-card-back" style={{ borderColor: project.color }}>
                      <div className="project-back-content">
                        <h4 style={{ color: project.color }}>Key Features</h4>
                        <ul className="project-features-list">
                          {project.features.slice(0, 4).map((feature, i) => (
                            <li key={i}>
                              <FontAwesomeIcon icon={faBolt} className="feature-icon" style={{ color: project.color }} />
                              {t(feature)}
                            </li>
                          ))}
                        </ul>
                        <button onClick={() => openModal(project)} className="project-link" style={{ borderColor: project.color, color: project.color }}>
                          <span>View Details</span>
                          <FontAwesomeIcon icon={faArrowRight} className="link-icon" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button className="pagination-arrow" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                {[...Array(totalPages).keys()].map(number => (
                  <button key={number + 1} onClick={() => paginate(number + 1)} className={currentPage === number + 1 ? 'active' : ''}>
                    {number + 1}
                  </button>
                ))}
                <button className="pagination-arrow" onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            )}
          </div>
        </section>

        {/* CV SECTION - Ongeza hii kabla ya contact section */}
        <section id="cv" className="section cv-section" ref={sectionRefs.cv}>
          <div className="container">
            <h2 className="section-title">
              <FontAwesomeIcon icon={faFileAlt} className="title-icon" />
              {t('my_cv')}
            </h2>
            <div className="cv-card">
              <div className="cv-preview">
                <div className="cv-icon-large">
                  <FontAwesomeIcon icon={faFilePdf} />
                </div>
                <h3>{t('download_my_cv')}</h3>
                <p>{t('cv_description')}</p>
                <div className="cv-buttons">
                  <a 
                    href="/Robetho-Lyando.pdf" 
                    download="Robetho_Lyando_CV.pdf"
                    className="cv-btn cv-download"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    <span>{t('download_cv')}</span>
                  </a>
                  <a 
                    href="/Robetho-Lyando.pdf" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cv-btn cv-view"
                  >
                    <FontAwesomeIcon icon={faEye} />
                    <span>{t('view_cv')}</span>
                  </a>
                </div>
              </div>
              
              <div className="cv-highlights">
                <h4>{t('cv_highlights')}</h4>
                <ul className="cv-highlights-list">
                  <li>
                    <FontAwesomeIcon icon={faGraduationCap} className="highlight-icon" />
                    <span>{t('bachelor_title')}</span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faBriefcase} className="highlight-icon" />
                    <span>{t('years_experience')}</span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faProjectDiagram} className="highlight-icon" />
                    <span>{t('projects_completed')}</span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faTrophy} className="highlight-icon" />
                    <span>{t('certifications')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT SECTION */}
        <section id="contact" className="section" ref={sectionRefs.contact}>
          <div className="container">
            <h2 className="section-title">
              <FontAwesomeIcon icon={faAddressBook} className="title-icon" />
              {t('contact')}
            </h2>
            <div className="contact-content-grid">
              <ContactForm />
              <div className="contact-info-grid">
                <div className="info-card">
                  <div className="info-icon"><FontAwesomeIcon icon={faPhone} /></div>
                  <h3>{t('call_me')}</h3>
                  <p><a href="tel:+255679778746">+255 679 778 746</a></p>
                  <p><a href="tel:+255689052127">+255 689 052 127</a></p>
                </div>
                <div className="info-card">
                  <div className="info-icon"><FontAwesomeIcon icon={faEnvelope} /></div>
                  <h3>{t('mail')}</h3>
                  <p><a href="mailto:robetholyando@gmail.com">robetholyando@gmail.com</a></p>
                  <p><a href="mailto:lyandorobetho@gmail.com">lyandorobetho@gmail.com</a></p>
                </div>
                <div className="info-card">
                  <div className="info-icon"><FontAwesomeIcon icon={faMapMarkerAlt} /></div>
                  <h3>{t('current_location')}</h3>
                  <p>{t('current_location_desc')}</p>
                  <a href="https://www.google.com/maps/place/ifakara,+Tanzania" target="_blank" rel="noopener noreferrer">
                    {t('view_on_maps')} <FontAwesomeIcon icon={faArrowRight} className="map-arrow" />
                  </a>
                </div>
                <div className="info-card">
                  <div className="info-icon"><FontAwesomeIcon icon={faMapMarkerAlt} /></div>
                  <h3>{t('second_location')}</h3>
                  <p>{t('second_location_desc')}</p>
                  <a href="https://www.google.com/maps/place/dar-es-salaam,+mabibo,+Tanzania" target="_blank" rel="noopener noreferrer">
                    {t('view_on_maps')} <FontAwesomeIcon icon={faArrowRight} className="map-arrow" />
                  </a>
                </div>
              </div>
            </div>
            
            {/* Appointment Scheduler */}
            <AppointmentScheduler />
          </div>
        </section>

        {/* FOOTER */}
        <footer className="footer">
          <div className="container">
            <p className="copyright">
              <FontAwesomeIcon icon={faHeart} className="footer-heart" />
              {t('copyright')}
            </p>
          </div>
        </footer>
      </main>
      
      {/* Project Modal */}
      <ProjectModal project={selectedProject} onClose={closeModal} />
    </div>
  );
};

const App = () => (
  <ThemeProvider>
    <AppContent />
  </ThemeProvider>
);

export default App;